import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Copy, Download, FileImage, FileText, LayoutDashboard, Link2, Share2 } from "lucide-react";
import type { ReactNode } from "react";
import { shareCanvas, unshareCanvas, type CanvasData } from "../../api";
import CanvasView from "./CanvasView";
import { downloadPdf, downloadPng } from "./exportCanvas";

interface CanvasVersion {
  id: string;
  canvas?: CanvasData | null;
  shareToken?: string | null;
  shareable?: boolean; // share links exist for saved Agent Studio messages only
}

interface Props {
  versions: CanvasVersion[]; // saved assistant messages that produced a canvas, oldest first
  selectedId: string | null;
  liveCanvas: CanvasData | null; // canvas streaming in for the reply in progress
  onSelect: (messageId: string) => void;
  onShareChange: (messageId: string, token: string | null) => void;
}

export const shareUrlFor = (token: string) => `${window.location.origin}/#/share/${token}`;

export function CanvasEmpty({ title = "Canvas", text, children }: { title?: string; text: string; children?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border-default-t bg-surface text-adrsta-blue">
        <LayoutDashboard className="h-7 w-7" />
      </div>
      <h2 className="mt-4 text-base font-bold text-primary-t">{title}</h2>
      <p className="mt-1 max-w-sm text-[13px] text-secondary-t">{text}</p>
      {children}
    </div>
  );
}

export default function CanvasPanel({ versions, selectedId, liveCanvas, onSelect, onShareChange }: Props) {
  const exportRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<"download" | "share" | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = versions.find((v) => v.id === selectedId) ?? null;
  const canvas = liveCanvas ?? selected?.canvas ?? null;
  const isLive = liveCanvas !== null;
  const shareUrl = selected?.shareToken ? shareUrlFor(selected.shareToken) : null;

  useEffect(() => {
    setMenu(null);
    setError(null);
  }, [selectedId, isLive]);

  if (!canvas) {
    return <CanvasEmpty text="Charts, tables, and reports from your analysis appear here. Attach data and ask a question to get started." />;
  }

  const runExport = async (kind: "png" | "pdf") => {
    if (!exportRef.current) return;
    setBusy(true);
    setMenu(null);
    try {
      if (kind === "png") await downloadPng(exportRef.current, canvas.title);
      else await downloadPdf(exportRef.current, canvas.title);
    } catch {
      setError("The download failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      if (selected.shareToken) {
        await unshareCanvas(selected.id);
        onShareChange(selected.id, null);
      } else {
        const { token } = await shareCanvas(selected.id);
        onShareChange(selected.id, token);
      }
    } catch {
      setError("Sharing failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border-subtle-t px-5 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[14px] font-bold text-primary-t">{canvas.title}</h2>
          {isLive ? (
            <p className="text-xs" style={{ color: "var(--warning)" }}>Updating…</p>
          ) : (
            versions.length > 1 && (
              <select
                value={selectedId ?? ""}
                onChange={(e) => onSelect(e.target.value)}
                className="mt-0.5 max-w-full border-none bg-transparent p-0 text-xs text-tertiary-t focus:outline-none"
              >
                {versions.map((v, i) => (
                  <option key={v.id} value={v.id}>
                    Version {i + 1} of {versions.length}: {v.canvas?.title}
                  </option>
                ))}
              </select>
            )
          )}
        </div>

        <div className="relative">
          <button
            disabled={busy || isLive}
            onClick={() => setMenu(menu === "download" ? null : "download")}
            className="flex items-center gap-1.5 rounded-lg border border-border-default-t px-3 py-1.5 text-[13px] font-semibold text-secondary-t hover:bg-hover-bg disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {menu === "download" && (
            <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-border-default-t bg-elevated py-1 shadow-lg">
              <MenuItem icon={FileImage} label="Image (PNG)" onClick={() => runExport("png")} />
              <MenuItem icon={FileText} label="Document (PDF)" onClick={() => runExport("pdf")} />
            </div>
          )}
        </div>

        <div className="relative">
          <button
            disabled={busy || isLive || !selected || selected.shareable === false}
            title={selected?.shareable === false ? "Share links are available in Agent Studio chats. Download instead." : undefined}
            onClick={() => setMenu(menu === "share" ? null : "share")}
            className="flex items-center gap-1.5 rounded-lg bg-adrsta-blue px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
          {menu === "share" && selected && (
            <div className="absolute right-0 z-20 mt-1 w-80 rounded-lg border border-border-default-t bg-elevated p-4 shadow-lg">
              <p className="text-[13px] font-semibold text-primary-t">Share this canvas</p>
              <p className="mt-1 text-xs text-tertiary-t">
                Anyone with the link can view this version read-only. Your chat is not shared.
              </p>
              {shareUrl ? (
                <>
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-border-default-t bg-surface px-2 py-1.5">
                    <Link2 className="h-4 w-4 shrink-0 text-tertiary-t" />
                    <span className="truncate text-xs text-secondary-t">{shareUrl}</span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={copyLink}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-adrsta-blue px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? "Copied" : "Copy link"}
                    </button>
                    <button
                      onClick={toggleShare}
                      disabled={busy}
                      className="rounded-lg border border-border-default-t px-3 py-1.5 text-[13px] font-semibold text-secondary-t hover:bg-hover-bg"
                    >
                      Stop sharing
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={toggleShare}
                  disabled={busy}
                  className="mt-3 w-full rounded-lg bg-adrsta-blue px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
                >
                  Create share link
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="mx-5 mt-3 rounded-lg px-3 py-2 text-[13px]" style={{ background: "var(--error-subtle)", color: "var(--error)" }}>
          {error}
        </p>
      )}

      <div className="flex-1 overflow-y-auto p-5">
        <div className="overflow-hidden rounded-card border border-border-default-t">
          <CanvasView ref={exportRef} canvas={canvas} />
        </div>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick }: { icon: typeof Download; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-primary-t hover:bg-hover-bg">
      <Icon className="h-4 w-4 text-tertiary-t" /> {label}
    </button>
  );
}
